import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import countries from '../../../commons/util/countries.json';

/*
 * State + interaction logic for the searchable country picker.
 * Handles the open/close panel, the search query and the keyboard-driven
 * active row, plus click-outside / Escape dismissal.
 */
function useCountrySelectState ({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [openUpward, setOpenUpward] = useState(false);

    const containerRef = useRef(null);
    const searchRef = useRef(null);
    const listRef = useRef(null);

    const selected = useMemo(
        () => countries.find((c) => c.iso2 === value) || null,
        [value]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return countries;
        return countries.filter(
            (c) => c.name.toLowerCase().includes(q) || c.iso2.toLowerCase().startsWith(q)
        );
    }, [query]);

    const close = useCallback(() => {
        setOpen(false);
        setQuery('');
    }, []);

    const select = useCallback((country) => {
        onChange(country.iso2);
        close();
    }, [onChange, close]);

    // Reset the active row to the top whenever the filtered set changes.
    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    // Suppress the parent scroll container's scrollbar before paint to avoid a
    // one-frame flash. useLayoutEffect fires synchronously after DOM mutation.
    useLayoutEffect(() => {
        if (!open) return undefined;
        let scrollParent = containerRef.current?.parentElement;
        while (scrollParent) {
            const { overflowY } = window.getComputedStyle(scrollParent);
            if (overflowY === 'auto' || overflowY === 'scroll') break;
            scrollParent = scrollParent.parentElement;
        }
        // Flip the panel upward against the scroll container's visible bottom
        // (which sits above the fixed footer), not the window — otherwise the
        // list opens down behind the footer and gets clipped. Decided here,
        // before the overflow is mutated below.
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const boundaryBottom = scrollParent
                ? scrollParent.getBoundingClientRect().bottom
                : window.innerHeight;
            setOpenUpward(boundaryBottom - rect.bottom < 300);
        }
        if (scrollParent) scrollParent.style.overflowY = 'hidden';
        return () => { if (scrollParent) scrollParent.style.overflowY = ''; };
    }, [open]);

    // Focus the search field and dismiss on click-outside / Escape while open.
    useEffect(() => {
        if (!open) return undefined;
        if (searchRef.current) searchRef.current.focus();

        // Position the selected country in the middle of the list box by setting
        // the list's own scrollTop (so the surrounding drawer doesn't move).
        // Query is empty on open, so the index maps to `countries`.
        const selectedIndex = countries.findIndex((c) => c.iso2 === value);
        if (selectedIndex >= 0) {
            setActiveIndex(selectedIndex);
            const list = listRef.current;
            const node = list && list.querySelector(`[data-index="${selectedIndex}"]`);
            if (node) {
                list.scrollTop = node.offsetTop - (list.clientHeight - node.offsetHeight) / 2;
            }
        }

        function handlePointer (e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                close();
            }
        }
        document.addEventListener('mousedown', handlePointer);
        return () => document.removeEventListener('mousedown', handlePointer);
    }, [open, value, close]);

    function handleKeyDown (e) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const country = filtered[activeIndex];
            if (country) select(country);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            close();
        }
    }

    return {
        open,
        setOpen,
        query,
        setQuery,
        activeIndex,
        setActiveIndex,
        containerRef,
        searchRef,
        listRef,
        openUpward,
        selected,
        filtered,
        select,
        close,
        handleKeyDown,
    };
}

export default useCountrySelectState;
